import { memo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Shield, Loader2, ArrowLeftRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { haptics } from "@/utils/haptics";
import { useAdminAccess } from "@/hooks/useAdminAccess";
import { PrefetchLink } from "@/components/PrefetchLink";
import { getNavItemsForSegment } from "@/lib/navigation/segmentNavItems";
import { useEffectiveSegmentDetailed } from "@/hooks/useEffectiveSegment";
import { setValidationCache, getValidationCache } from "@/hooks/useTokenValidation";

/**
 * Clickable Admin pill in the top nav.
 * - When in user view: clicking jumps to /admin (Admin Hub).
 * - When already inside /admin: clicking returns to user view ("Exit admin").
 * Visible only to authenticated admins / super admins.
 */
const AdminToggle = memo(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, isSuperAdmin, isReady, isAuthenticated, allRoles, userEmail, profileId } =
    useAdminAccess();

  const isAdminView = location.pathname.startsWith("/admin");
  const canAccess = isAdmin || isSuperAdmin;

  const handleToggle = useCallback(() => {
    haptics.light();
    if (isAdminView) {
      navigate("/");
      return;
    }
    // Seed admin validation cache from the existing user cache so AccessGate
    // and RequirePermission see a fully-populated admin context on first
    // render — preventing the "not found / login bounce" flash.
    try {
      localStorage.setItem("admin_navigation_grace", Date.now().toString());
      const userCache = getValidationCache("user");
      if (userCache?.isValid) {
        setValidationCache("admin", {
          ...userCache,
          isAdmin: true,
        });
      } else if (userEmail) {
        setValidationCache("admin", {
          isValid: true,
          isAdmin: true,
          userEmail,
          profileId: profileId ?? null,
          userName: null,
          paymentStatus: null,
          expiresAt: null,
          usesRemaining: null,
          failureReason: null,
        } as any);
      }
    } catch {
      /* non-blocking */
    }
    navigate("/admin");
  }, [isAdminView, navigate, userEmail, profileId]);

  if (isAuthenticated && !isReady) {
    return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground ml-2" />;
  }
  if (!isReady || !canAccess) return null;

  const label = isAdminView ? "Exit Admin" : isSuperAdmin ? "Super Admin" : "Admin Hub";
  const title = isAdminView
    ? "Return to user view"
    : `Open Admin Hub (${allRoles.join(", ") || "admin"}) — shortcut Alt+A`;

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleToggle}
      title={title}
      aria-label={title}
      className={cn(
        "ml-2 h-8 gap-1.5 rounded-2xl border transition-all",
        isAdminView
          ? "bg-primary/10 border-primary/30 text-primary hover:bg-primary/15"
          : "bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-400 dark:hover:bg-amber-900/40",
      )}
    >
      {isAdminView ? <ArrowLeftRight className="h-3.5 w-3.5" /> : <Shield className="h-3.5 w-3.5" />}
      {label}
    </Button>
  );
});
AdminToggle.displayName = "AdminToggle";

export const DesktopNav = () => {
  const location = useLocation();
  const { segment, isReady } = useEffectiveSegmentDetailed();
  const navItems = getNavItemsForSegment(segment, { mobile: false });

  if (!isReady) {
    return (
      <nav className="hidden md:flex items-center gap-2" aria-busy="true">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-8 w-20 rounded-2xl bg-muted/50 animate-pulse" />
        ))}
      </nav>
    );
  }

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    const base = path.split("?")[0];
    return location.pathname === base || location.pathname.startsWith(base + "/");
  };

  return (
    <nav className="hidden md:flex items-center gap-2">
      {navItems.map((item) => {
        const Icon = item.icon;
        return (
          <PrefetchLink key={item.path} to={item.path}>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "rounded-2xl transition-all",
                isActive(item.path)
                  ? "bg-primary/10 text-primary font-semibold shadow-sm"
                  : "text-foreground/80 hover:text-foreground hover:bg-muted/50"
              )}
              onClick={() => haptics.light()}
            >
              <Icon className="h-4 w-4 mr-2" />
              {item.label}
            </Button>
          </PrefetchLink>
        );
      })}

      <AdminToggle />
    </nav>
  );
};
